<script setup lang="ts">
import {computed, ref} from "vue";
import {
    ChevronDown,
    CircleAlert,
    Hand,
    History,
    ShieldAlert,
    ShieldCheck,
    ShieldQuestion,
    ShieldX,
    TriangleAlert
} from "@lucide/vue";
import AssistantPropositionDecision from "@/ai-assistant/components/AssistantPropositionDecision.vue";
import AssistantPropositionJustification from "@/ai-assistant/components/AssistantPropositionJustification.vue";
import {Proposition, PropositionViolation} from "@/ai-assistant/interfaces";
import {
    CONFORMITY_LABELS,
    FOLDED_SEVERITIES,
    MANUAL_OPERATION_LABELS,
    PROPOSITION_STATE_LABELS
} from "@/ai-assistant/constants";
import {measure, sourceLabel} from "@/ai-assistant/lib/propositionFormat";

const {proposition, index} = defineProps<{
    proposition: Proposition;
    /** Rank in the answer, so a scenario without a name still has one. */
    index: number;
}>();

const expanded = ref<boolean>(false);
const comparing = ref<boolean>(false);

const emits = defineEmits<{
    (event: 'adjust', proposition: Proposition): void;
}>();

/** Where the scenario stands, shown once it has been decided. */
const stateLabel = computed<string>(() => PROPOSITION_STATE_LABELS[proposition.state] ?? '');

/**
 * Not decided yet: the pharmacist can still compare and choose.
 *
 * Read from the state itself, not from the absence of a label: the history
 * names every state, « Proposé » included.
 */
const undecided = computed<boolean>(() => ['draft', 'proposed'].includes(proposition.state));

/** The strategy that produced it, when the solver named one. */
const title = computed<string>(() => {
    const origin = proposition.author?.origin;

    return typeof origin === 'string' && origin ? origin : `Scénario ${index + 1}`;
});

const status = computed<string>(() => proposition.conformity?.status ?? 'unverifiable');

const conformityLabel = computed<string>(() =>
    CONFORMITY_LABELS[status.value] ?? 'Conformité inconnue'
);

const conformityIcon = computed(() => ({
    compliant: ShieldCheck,
    alert: ShieldAlert,
    non_compliant: ShieldX
}[status.value] ?? ShieldQuestion));

/** An unrecognised verdict falls back to the neutral tone, never to none. */
const conformityTone = computed<string>(() => ({
    compliant: 'bg-secondary-lightest text-secondary-dark',
    alert: 'bg-orange/20 text-gray-dark',
    non_compliant: 'bg-orange/30 text-gray-dark'
}[status.value] ?? 'bg-gray-light text-gray-dark/70'));

/**
 * Anything above the pharmacist's own instructions is shown outside the fold.
 *
 * Folded away it would be found by whoever went looking, which is not the
 * person who needs it.
 */
const outstanding = computed<PropositionViolation[]>(() =>
    (proposition.conformity?.violations ?? [])
        .filter(violation => ! FOLDED_SEVERITIES.includes(violation.severity))
);

const informational = computed<PropositionViolation[]>(() =>
    (proposition.conformity?.violations ?? [])
        .filter(violation => FOLDED_SEVERITIES.includes(violation.severity))
);

/**
 * A scenario computed on a planning that has since moved.
 *
 * The server re-checks this when the proposition is read, so a card reopened
 * an hour later says so rather than offering a diff against a state that no
 * longer exists. Shown outside the fold: it changes whether the scenario means
 * anything at all.
 */
const expired = computed<boolean>(() => proposition.state === 'expired');

/** Rules that did not run. Silence from a rule is not a pass. */
const unevaluated = computed(() => proposition.conformity?.unevaluated ?? []);

const manualLabel = (op: string): string =>
    MANUAL_OPERATION_LABELS[op] ?? 'Action à réaliser vous-même';

</script>

<template>
  <article class="rounded-lg border border-primary-lightest bg-white">
    <header class="flex items-start gap-2 border-b border-primary-lightest px-3 py-2">
      <p class="min-w-0 flex-1 text-xs font-semibold text-primary">
        {{ title }}
      </p>
      <span
        class="flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
        :class="conformityTone"
      >
        <component :is="conformityIcon" class="size-3 shrink-0" />
        {{ conformityLabel }}
      </span>
    </header>

    <div class="space-y-2.5 px-3 py-2.5">
      <!--
        What it does not solve comes before what it does. The order is
        deliberate and reads backwards: presenting a scenario as complete when
        it leaves a hole is the gravest fault this module can commit, and a
        pharmacist who believes their Saturday is covered will not look again.
      -->
      <section v-if="proposition.justification?.limits?.length">
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
          Ce que ce scénario ne résout pas
        </p>
        <ul class="space-y-1">
          <li
            v-for="(limit, limitIndex) in proposition.justification.limits"
            :key="`limite-${limitIndex}`"
            class="flex items-start gap-1.5 text-[11px] leading-snug text-gray-dark/80"
          >
            <CircleAlert class="mt-px size-3 shrink-0 text-orange" />
            <span class="min-w-0 flex-1">{{ limit }}</span>
          </li>
        </ul>
      </section>

      <section
        v-if="expired"
        class="flex items-start gap-1.5 rounded-lg bg-gray-light px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
      >
        <History class="mt-px size-3.5 shrink-0 text-gray-dark/60" />
        <span class="min-w-0 flex-1">
          Le planning a changé depuis que ce scénario a été calculé : il ne
          décrit plus l'état auquel il s'appliquerait. Reposez la question pour
          en obtenir un à jour.
        </span>
      </section>

      <!-- The source travels with the alert: the pharmacist checks for
           themselves, which is the point of showing it. -->
      <section
        v-for="violation in outstanding"
        :key="violation.rule_id"
        class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
      >
        <TriangleAlert class="mt-px size-3.5 shrink-0 text-orange" />
        <span class="min-w-0 flex-1">
          {{ violation.label }}
          <span v-if="sourceLabel(violation.source)" class="text-gray-dark/60">
            — {{ sourceLabel(violation.source) }}
          </span>
        </span>
      </section>

      <section v-if="proposition.impacts?.length">
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
          Ce que cela change
        </p>
        <ul class="space-y-1">
          <li
            v-for="(impact, impactIndex) in proposition.impacts"
            :key="`impact-${impactIndex}`"
            class="flex items-baseline justify-between gap-2 text-[11px] leading-snug"
          >
            <span class="min-w-0 flex-1 text-gray-dark/80">{{ impact.metric }}</span>
            <span class="shrink-0 tabular-nums text-gray-dark/60">{{ measure(impact.before, impact.unit) }}</span>
            <span class="shrink-0 text-gray-dark/40">→</span>
            <span class="shrink-0 font-semibold tabular-nums text-gray-dark">{{ measure(impact.after, impact.unit) }}</span>
          </li>
        </ul>
      </section>

      <!--
        Carried and displayed, never written: the appointments live on the
        health server, and a proposition that claimed to have moved one would
        be saying something false about the agenda.
      -->
      <section v-if="proposition.manual_operations?.length">
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
          À faire vous-même
        </p>
        <ul class="space-y-1">
          <li
            v-for="(operation, operationIndex) in proposition.manual_operations"
            :key="`manuelle-${operationIndex}`"
            class="flex items-start gap-1.5 text-[11px] leading-snug text-gray-dark/80"
          >
            <Hand class="mt-px size-3 shrink-0 text-primary/60" />
            <span class="min-w-0 flex-1">{{ manualLabel(operation.op) }}</span>
          </li>
        </ul>
      </section>

      <div class="flex flex-wrap items-center justify-between gap-2">
        <button
          class="flex items-center gap-1 text-[11px] text-primary/80 transition-colors hover:text-primary"
          :aria-expanded="expanded"
          @click="expanded = !expanded"
        >
          <ChevronDown class="size-3 transition-transform" :class="expanded && 'rotate-180'" />
          Expliquer cette recommandation
        </button>

        <!--
          Comparing and deciding are one screen: a pharmacist who has read what
          changes should not have to find the decision elsewhere. Offered even
          on an expired scenario, whose modal explains why it can no longer be
          applied rather than hiding the button and leaving them guessing.
        -->
        <button
          v-if="undecided || expired"
          class="rounded-full border border-primary/30 px-2.5 py-0.5 text-[11px] text-primary transition-colors hover:border-primary/60"
          @click="comparing = true"
        >
          Comparer et décider
        </button>
        <span
          v-else-if="stateLabel"
          class="rounded-full bg-gray-light px-2 py-0.5 text-[10px] text-gray-dark/70"
        >{{ stateLabel }}</span>
      </div>

      <!--
        Re-reads the justification already received; it never asks for a second
        answer, which could contradict the scenario displayed above.
      -->
      <template v-if="expanded">
        <AssistantPropositionJustification
          v-if="proposition.justification"
          :justification="proposition.justification"
        />

        <div v-if="informational.length || unevaluated.length" class="space-y-1 pt-0.5">
          <p
            v-for="violation in informational"
            :key="violation.rule_id"
            class="text-[10.5px] leading-snug text-gray-dark/70"
          >
            {{ violation.label }}<template v-if="sourceLabel(violation.source)">
              — {{ sourceLabel(violation.source) }}
            </template>
          </p>
          <p
            v-for="rule in unevaluated"
            :key="rule.rule_id"
            class="text-[10.5px] leading-snug text-gray-dark/60"
          >
            {{ rule.reason }}
          </p>
        </div>
      </template>
    </div>

    <AssistantPropositionDecision
      v-model:open="comparing"
      :proposition="proposition"
      @adjust="emits('adjust', proposition)"
    />
  </article>
</template>
